import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Empty,
  Spin,
  theme,
  Row,
  Col,
  Segmented,
  message,
  Switch,
  Select,
} from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import LocationColorLegend from "./LocationColorLegend";
import { LocationType } from "../../modal/location-types";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { CompanyContext } from "../../../../contexts/company-context";
import { ScheduleOperation } from "../../../../serviceoperation/ScheduleOperation";
import { Result } from "../../../../serviceoperation/entity/ScheduleOperationEntity";
import { timeOffStatus } from "../../../../constants";
import "./timeline-view.css";

dayjs.extend(isBetween);

const { Text } = Typography;

interface TimelineShift {
  scheduleID: number;
  employeeID: string;
  employeeName: string;
  startTime: Date;
  endTime: Date;
  color: string;
  type: "shift" | "timeoff" | "timeoff-pending";
  branch?: string;
  note?: string;
  title?: string;
  isPublished?: boolean;
}

interface TimelineEmployee {
  employeeID: string;
  employeeName: string;
  shifts: TimelineShift[];
}

interface TimelineViewProps {
  selectedLocation?: string;
  onLocationChange?: (location: string) => void;
  availableLocations?: LocationType[];
  currentWeek?: Date;
  onWeekChange?: (direction: "prev" | "next" | "current") => void;
}

const ScheduleTimelineView: React.FC<TimelineViewProps> = ({
  selectedLocation,
  onLocationChange,
  availableLocations = [],
  currentWeek = new Date(),
  onWeekChange,
}) => {
  const { token } = theme.useToken();
  const { selectedCompany } = useContext(CompanyContext);
  const scheduleOps = useMemo(() => new ScheduleOperation(), []);

  const [loading, setLoading] = useState(false);
  const [timelineData, setTimelineData] = useState<TimelineEmployee[]>([]);
  const [viewMode, setViewMode] = useState<"day" | "week-summary">("day");
  const [selectedDay, setSelectedDay] = useState<number>(new Date().getDay());
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [draggingShift, setDraggingShift] = useState<{
    scheduleID: number;
    startX: number;
    originalStart: Date;
    originalEnd: Date;
    dragType: "move" | "resize-start" | "resize-end";
  } | null>(null);
  const [dragPreview, setDragPreview] = useState<{
    startTime: Date;
    endTime: Date;
  } | null>(null);
  const [updatingShift, setUpdatingShift] = useState<number | null>(null);
  const [showColorLegend, setShowColorLegend] = useState(false);
  const previousTimelineRef = useRef<TimelineEmployee[] | null>(null);
  const timelineRightRef = useRef<HTMLDivElement | null>(null);
  const timelineWidthRef = useRef<number>(0);
  const dragRafRef = useRef<number | null>(null);
  const lastDeltaMinutesRef = useRef<number | null>(null);
  const didInitSelectedDayRef = useRef(false);

  // Auto-update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Ensure current date is selected on initial load
  useEffect(() => {
    if (didInitSelectedDayRef.current) return;
    setSelectedDay(new Date().getDay());
    didInitSelectedDayRef.current = true;
  }, []);

  // Calculate week dates
  const weekStart = useMemo(() => {
    const start = dayjs(currentWeek).startOf("week");
    return start;
  }, [currentWeek]);

  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));
  }, [weekStart]);

  // Function to load schedule data
  const loadScheduleData = async () => {
    if (!selectedCompany?.corporationId) return;

    setLoading(true);
    try {
      const weekOf = weekStart.format("YYYY-MM-DD");
      const response = await scheduleOps.getScheduleAsync(
        selectedCompany.corporationId,
        {
          weekOf,
          page: 1,
          pageSize: 500,
          ...(selectedLocation &&
            selectedLocation !== "0" &&
            selectedLocation !== "all" && {
              branchId: selectedLocation,
            }),
        },
      );

      const transformedData = transformScheduleData(response.results);
      setTimelineData(transformedData);
    } catch (error) {
      console.error("Error fetching schedule data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch schedule data
  useEffect(() => {
    loadScheduleData();
  }, [selectedCompany, selectedLocation, weekStart, scheduleOps]);

  useEffect(() => {
    const updateTimelineWidth = () => {
      timelineWidthRef.current =
        timelineRightRef.current?.getBoundingClientRect().width || 0;
    };

    updateTimelineWidth();
    window.addEventListener("resize", updateTimelineWidth);

    return () => {
      window.removeEventListener("resize", updateTimelineWidth);
    };
  }, []);

  const getDeltaMinutesFromPixels = (deltaX: number) => {
    const width = timelineWidthRef.current;
    if (!width) return 0;
    const minutes = (deltaX / width) * 24 * 60;
    return Math.round(minutes / 6) * 6;
  };

  // Extract unique locations/branches from timeline data for legend
  const locations: LocationType[] = useMemo(() => {
    const branchMap = new Map<string, { name: string; color: string }>();
    timelineData.forEach((employee) => {
      employee.shifts.forEach((shift) => {
        if (shift.branch && shift.color) {
          branchMap.set(shift.branch, {
            name: shift.branch,
            color: shift.color,
          });
        }
      });
    });
    return Array.from(branchMap.entries()).map(([key, value]) => ({
      key,
      name: value.name,
      color: value.color,
    }));
  }, [timelineData]);

  const transformScheduleData = (results: Result[]): TimelineEmployee[] => {
    const employeeMap = new Map<string, TimelineEmployee>();

    results.forEach((result) => {
      const employeeID = result.employee.employeeID;
      const employeeName = `${result.employee.firstName} ${result.employee.lastName}`;

      if (!employeeMap.has(employeeID)) {
        employeeMap.set(employeeID, {
          employeeID,
          employeeName,
          shifts: [],
        });
      }

      const workDate = dayjs(result.workDate);
      const startTime = workDate
        .hour(result.startWorkHour)
        .minute(result.startWorkMinute)
        .toDate();
      const endTime = workDate
        .hour(result.endWorkHour)
        .minute(result.endWorkMinute)
        .toDate();

      let shiftType: "shift" | "timeoff" | "timeoff-pending" = "shift";
      if (result.timeoffDetail) {
        shiftType =
          result.timeoffDetail.timeOffStatusId === timeOffStatus.APPROVE
            ? "timeoff"
            : "timeoff-pending";
      }

      employeeMap.get(employeeID)?.shifts.push({
        scheduleID: result.scheduleID,
        employeeID,
        employeeName,
        startTime,
        endTime,
        color: result.branch?.color || "#1890ff",
        type: shiftType,
        branch: result.branch?.name,
        note: result.note,
        title: result.title,
        isPublished: !(result.isDraft ?? false),
      });
    });

    // Sort employees by name
    return Array.from(employeeMap.values()).sort((a, b) =>
      a.employeeName.localeCompare(b.employeeName),
    );
  };

  const getShiftPositionAndWidth = (
    shift: TimelineShift,
    dayDate: dayjs.Dayjs,
  ) => {
    const dayStart = dayDate.hour(0).minute(0);
    const shiftStart = dayjs(shift.startTime);
    const shiftEnd = dayjs(shift.endTime);

    // Check if shift is on this day
    if (!shiftStart.isSame(dayDate, "day")) return null;

    // Calculate position (percentage from start of day)
    const minutesFromStart = shiftStart.diff(dayStart, "minute");
    const totalMinutesInDay = 24 * 60;
    const left = (minutesFromStart / totalMinutesInDay) * 100;

    // Calculate width (percentage of day)
    const shiftDuration = shiftEnd.diff(shiftStart, "minute");
    const width = (shiftDuration / totalMinutesInDay) * 100;

    return { left: `${left}%`, width: `${width}%` };
  };

  const handleShiftMouseDown = (
    e: React.MouseEvent,
    shift: TimelineShift,
    dragType: "move" | "resize-start" | "resize-end",
  ) => {
    if (shift.type !== "shift") return; // Only allow dragging regular shifts
    e.preventDefault();
    timelineWidthRef.current =
      timelineRightRef.current?.getBoundingClientRect().width ||
      timelineWidthRef.current;
    setDraggingShift({
      scheduleID: shift.scheduleID,
      startX: e.clientX,
      originalStart: shift.startTime,
      originalEnd: shift.endTime,
      dragType,
    });
  };

  useEffect(() => {
    if (!draggingShift) return;

    const applyPreviewFromDelta = (deltaMinutes: number) => {
      let newStart = draggingShift.originalStart;
      let newEnd = draggingShift.originalEnd;

      if (draggingShift.dragType === "move") {
        newStart = dayjs(draggingShift.originalStart)
          .add(deltaMinutes, "minute")
          .toDate();
        newEnd = dayjs(draggingShift.originalEnd)
          .add(deltaMinutes, "minute")
          .toDate();
      } else if (draggingShift.dragType === "resize-start") {
        const temp = dayjs(draggingShift.originalStart)
          .add(deltaMinutes, "minute")
          .toDate();
        if (dayjs(temp).isBefore(draggingShift.originalEnd)) {
          newStart = temp;
        }
      } else if (draggingShift.dragType === "resize-end") {
        const temp = dayjs(draggingShift.originalEnd)
          .add(deltaMinutes, "minute")
          .toDate();
        if (dayjs(temp).isAfter(draggingShift.originalStart)) {
          newEnd = temp;
        }
      }

      setDragPreview({ startTime: newStart, endTime: newEnd });
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingShift) return;
      const deltaX = e.clientX - draggingShift.startX;
      const deltaMinutes = getDeltaMinutesFromPixels(deltaX);

      if (lastDeltaMinutesRef.current === deltaMinutes) return;
      lastDeltaMinutesRef.current = deltaMinutes;

      if (dragRafRef.current !== null) return;
      dragRafRef.current = window.requestAnimationFrame(() => {
        applyPreviewFromDelta(deltaMinutes);
        dragRafRef.current = null;
      });
    };

    const handleMouseUp = async (e: MouseEvent) => {
      if (!draggingShift) return;

      const deltaX = e.clientX - draggingShift.startX;
      const deltaMinutes = getDeltaMinutesFromPixels(deltaX);

      let newStart = draggingShift.originalStart;
      let newEnd = draggingShift.originalEnd;

      if (draggingShift.dragType === "move") {
        newStart = dayjs(draggingShift.originalStart)
          .add(deltaMinutes, "minute")
          .toDate();
        newEnd = dayjs(draggingShift.originalEnd)
          .add(deltaMinutes, "minute")
          .toDate();
      } else if (draggingShift.dragType === "resize-start") {
        const temp = dayjs(draggingShift.originalStart)
          .add(deltaMinutes, "minute")
          .toDate();
        if (dayjs(temp).isBefore(draggingShift.originalEnd)) {
          newStart = temp;
        }
      } else if (draggingShift.dragType === "resize-end") {
        const temp = dayjs(draggingShift.originalEnd)
          .add(deltaMinutes, "minute")
          .toDate();
        if (dayjs(temp).isAfter(draggingShift.originalStart)) {
          newEnd = temp;
        }
      }

      // Only update if there was significant movement
      if (Math.abs(deltaX) > 10) {
        setUpdatingShift(draggingShift.scheduleID);
        previousTimelineRef.current = timelineData;
        setTimelineData((prev) =>
          prev.map((employee) => ({
            ...employee,
            shifts: employee.shifts.map((shift) =>
              shift.scheduleID === draggingShift.scheduleID
                ? { ...shift, startTime: newStart, endTime: newEnd }
                : shift,
            ),
          })),
        );
        try {
          if (selectedCompany?.corporationId) {
            await scheduleOps.putScheduleAsync(
              selectedCompany.corporationId,
              draggingShift.scheduleID,
              {
                StartWorkHour: newStart.getHours(),
                StartWorkMinute: newStart.getMinutes(),
                EndWorkHour: newEnd.getHours(),
                EndWorkMinute: newEnd.getMinutes(),
              },
            );
          }
        } catch (error) {
          console.error("Error updating shift:", error);
          if (previousTimelineRef.current) {
            setTimelineData(previousTimelineRef.current);
          }
          message.error("Failed to update schedule");
        } finally {
          setUpdatingShift(null);
        }
      }

      setDraggingShift(null);
      setDragPreview(null);
      lastDeltaMinutesRef.current = null;
      if (dragRafRef.current !== null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      setDragPreview(null);
      lastDeltaMinutesRef.current = null;
      if (dragRafRef.current !== null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
    };
  }, [draggingShift]);

  const renderHourMarkers = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const selectedDate = weekDates[selectedDay];
    const isToday = currentTime.isSame(selectedDate, "day");

    // Calculate current time position
    let currentTimePosition = null;
    if (isToday) {
      const minutesFromStart = currentTime.diff(
        currentTime.startOf("day"),
        "minute",
      );
      const totalMinutesInDay = 24 * 60;
      currentTimePosition = (minutesFromStart / totalMinutesInDay) * 100;
    }

    return (
      <div className="timeline-hour-markers">
        {hours.map((hour) => (
          <div
            key={hour}
            className="timeline-hour-marker"
            style={{
              left: `${(hour / 24) * 100}%`,
              backgroundColor: token.colorBorder,
            }}
          >
            <span
              className="timeline-hour-label"
              style={{ color: token.colorTextSecondary }}
            >
              {hour === 0
                ? "12 AM"
                : hour < 12
                ? `${hour} AM`
                : hour === 12
                ? "12 PM"
                : `${hour - 12} PM`}
            </span>
          </div>
        ))}
        {isToday && currentTimePosition !== null && (
          <div
            className="timeline-current-time"
            style={{ left: `${currentTimePosition}%` }}
          >
            <div className="timeline-current-time-label">
              {currentTime.format("h:mm A")}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDragPreview = (
    dayDate: dayjs.Dayjs,
    draggedShift: TimelineShift,
  ) => {
    if (!dragPreview) return null;

    const position = getShiftPositionAndWidth(
      {
        ...draggedShift,
        startTime: dragPreview.startTime,
        endTime: dragPreview.endTime,
      },
      dayDate,
    );

    if (!position) return null;

    return (
      <div
        className="timeline-shift timeline-shift-preview"
        style={{
          left: position.left,
          width: position.width,
          backgroundColor: draggedShift.color,
          borderColor: token.colorBorder,
          opacity: 0.4,
          border: `2px dashed ${draggedShift.color}`,
        }}
      >
        <div className="timeline-drag-tooltip">
          <ClockCircleOutlined style={{ marginRight: 6 }} />
          <span>
            {dayjs(dragPreview.startTime).format("h:mm A")} -{" "}
            {dayjs(dragPreview.endTime).format("h:mm A")}
          </span>
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const selectedDate = weekDates[selectedDay];
    const shiftsForDay = timelineData
      .map((employee) => ({
        ...employee,
        shifts: employee.shifts.filter((shift) =>
          dayjs(shift.startTime).isSame(selectedDate, "day"),
        ),
      }))
      .filter((employee) => employee.shifts.length > 0)
      .sort((a, b) => {
        const aStart = a.shifts.reduce((min, s) => {
          const start = dayjs(s.startTime);
          return start.isBefore(min) ? start : min;
        }, dayjs(a.shifts[0].startTime));

        const bStart = b.shifts.reduce((min, s) => {
          const start = dayjs(s.startTime);
          return start.isBefore(min) ? start : min;
        }, dayjs(b.shifts[0].startTime));

        const aEnd = a.shifts.reduce((max, s) => {
          const start = dayjs(s.startTime);
          const end = dayjs(s.endTime);
          const normalizedEnd = end.isBefore(start) ? end.add(1, "day") : end;
          return normalizedEnd.isAfter(max) ? normalizedEnd : max;
        }, dayjs(a.shifts[0].endTime));

        const bEnd = b.shifts.reduce((max, s) => {
          const start = dayjs(s.startTime);
          const end = dayjs(s.endTime);
          const normalizedEnd = end.isBefore(start) ? end.add(1, "day") : end;
          return normalizedEnd.isAfter(max) ? normalizedEnd : max;
        }, dayjs(b.shifts[0].endTime));

        if (aStart.isBefore(bStart)) return -1;
        if (aStart.isAfter(bStart)) return 1;
        if (aEnd.isBefore(bEnd)) return -1;
        if (aEnd.isAfter(bEnd)) return 1;
        return a.employeeName.localeCompare(b.employeeName);
      });

    if (shiftsForDay.length === 0) {
      return (
        <Empty
          description={`No shifts scheduled for ${selectedDate.format(
            "dddd, MMMM D",
          )}`}
          style={{ marginTop: 40 }}
        />
      );
    }

    return (
      <div
        className="timeline-container"
        style={{ background: token.colorBgContainer }}
      >
        <div
          className="timeline-header"
          style={{ borderBottomColor: token.colorBorder }}
        >
          <div
            className="timeline-header-left"
            style={{
              background: token.colorBgLayout,
              borderRightColor: token.colorBorder,
              color: token.colorText,
            }}
          >
            <CalendarOutlined style={{ marginRight: 8 }} />
            <Text strong>{selectedDate.format("dddd, MMMM D, YYYY")}</Text>
          </div>
          <div className="timeline-header-right" ref={timelineRightRef}>
            {renderHourMarkers()}
          </div>
        </div>

        <div className="timeline-employees">
          {shiftsForDay.map((employee, index) => (
            <div
              key={employee.employeeID}
              className="timeline-employee-row"
              style={{
                animationDelay: `${index * 0.05}s`,
                background: token.colorBgContainer,
                borderColor: token.colorBorder,
              }}
            >
              <div
                className="timeline-employee-name"
                style={{
                  background: token.colorBgLayout,
                  borderRightColor: token.colorBorder,
                  color: token.colorText,
                }}
              >
                <UserOutlined style={{ marginRight: 8 }} />
                <Text strong>{employee.employeeName}</Text>
              </div>
              <div className="timeline-employee-shifts">
                {employee.shifts.map((shift) => {
                  const position = getShiftPositionAndWidth(
                    shift,
                    selectedDate,
                  );
                  if (!position) return null;
                  const isUnpublished = shift.isPublished === false;

                  // Use branch color for regular shifts, standard colors for time off
                  const getShiftColor = () => {
                    if (shift.type === "timeoff") return "#52c41a";
                    if (shift.type === "timeoff-pending") return "#faad14";
                    return shift.color; // Use branch color
                  };

                  return (
                    <div
                      key={shift.scheduleID}
                      className={`timeline-shift timeline-shift-${shift.type}`}
                      style={{
                        left: position.left,
                        width: position.width,
                        backgroundColor: getShiftColor(),
                        borderColor: token.colorBorder,
                        borderStyle: isUnpublished ? "dashed" : "solid",
                        cursor: shift.type === "shift" ? "grab" : "default",
                        opacity:
                          updatingShift === shift.scheduleID
                            ? 0.6
                            : isUnpublished
                            ? 0.8
                            : 1,
                      }}
                      onMouseDown={(e) =>
                        handleShiftMouseDown(e, shift, "move")
                      }
                      onMouseEnter={(e) => {
                        if (shift.type === "shift") {
                          (e.currentTarget as HTMLElement).style.cursor =
                            draggingShift ? "grabbing" : "grab";
                        }
                      }}
                    >
                      {/* Resize handle - left */}
                      {shift.type === "shift" && (
                        <div
                          className="timeline-shift-resize-handle timeline-shift-resize-start"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleShiftMouseDown(e, shift, "resize-start");
                          }}
                          style={{ cursor: "col-resize" }}
                        />
                      )}
                      <div className="timeline-shift-content">
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        <Text style={{ color: "#fff", fontSize: 12 }} ellipsis>
                          {dayjs(shift.startTime).format("h:mm A")} -{" "}
                          {dayjs(shift.endTime).format("h:mm A")}
                        </Text>
                        {isUnpublished && (
                          <Text
                            style={{
                              color: "#fff",
                              fontSize: 10,
                              marginLeft: 6,
                            }}
                          >
                            Draft
                          </Text>
                        )}
                      </div>
                      {/* Resize handle - right */}
                      {shift.type === "shift" && (
                        <div
                          className="timeline-shift-resize-handle timeline-shift-resize-end"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleShiftMouseDown(e, shift, "resize-end");
                          }}
                          style={{ cursor: "col-resize" }}
                        />
                      )}
                    </div>
                  );
                })}
                {draggingShift &&
                  draggingShift.dragType !== undefined &&
                  dragPreview &&
                  employee.shifts.some(
                    (shift) => shift.scheduleID === draggingShift.scheduleID,
                  ) &&
                  (() => {
                    const draggedShift = employee.shifts.find(
                      (shift) => shift.scheduleID === draggingShift.scheduleID,
                    );
                    return draggedShift
                      ? renderDragPreview(selectedDate, draggedShift)
                      : null;
                  })()}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderManagerWeekView = () => {
    if (timelineData.length === 0) {
      return (
        <Empty
          description="No shifts scheduled for this week"
          style={{ marginTop: 40 }}
        />
      );
    }

    const daySummaries = weekDates.map((date, dayIndex) => {
      const dayShifts = timelineData.flatMap((employee) =>
        employee.shifts.filter((shift) =>
          dayjs(shift.startTime).isSame(date, "day"),
        ),
      );

      const scheduledShifts = dayShifts.filter((s) => s.type === "shift");
      const pendingTimeOff = dayShifts.filter(
        (s) => s.type === "timeoff-pending",
      );
      const approvedTimeOff = dayShifts.filter((s) => s.type === "timeoff");

      const totalMinutes = scheduledShifts.reduce((sum, s) => {
        const start = dayjs(s.startTime);
        const end = dayjs(s.endTime);
        const normalizedEnd = end.isBefore(start) ? end.add(1, "day") : end;
        return sum + normalizedEnd.diff(start, "minute");
      }, 0);

      const headcount = new Set(scheduledShifts.map((s) => s.employeeID)).size;

      const topEmployees = Array.from(
        new Set(scheduledShifts.map((s) => s.employeeName)),
      );

      return {
        date,
        dayIndex,
        headcount,
        totalHours: totalMinutes / 60,
        pendingCount: pendingTimeOff.length,
        approvedCount: approvedTimeOff.length,
        topEmployees,
      };
    });

    return (
      <Row gutter={[16, 16]}>
        {daySummaries.map((day) => (
          <Col key={day.dayIndex} xs={24} sm={12} lg={8}>
            <Card
              style={{
                borderRadius: 12,
                backgroundColor: token.colorBgContainer,
                borderColor: token.colorBorder,
                height: "100%",
              }}
              bodyStyle={{ padding: 16 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <Text strong style={{ fontSize: 14 }}>
                    {day.date.format("dddd")}
                  </Text>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {day.date.format("MMM D")}
                  </div>
                </div>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedDay(day.dayIndex);
                    setViewMode("day");
                  }}
                >
                  View Day
                </Button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginTop: 12,
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 11, color: token.colorTextSecondary }}
                  >
                    Scheduled
                  </div>
                  <Text strong style={{ fontSize: 16 }}>
                    {day.headcount} ppl
                  </Text>
                </div>
                <div>
                  <div
                    style={{ fontSize: 11, color: token.colorTextSecondary }}
                  >
                    Total Hours
                  </div>
                  <Text strong style={{ fontSize: 16 }}>
                    {day.totalHours.toFixed(1)}h
                  </Text>
                </div>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <div
                  style={{
                    padding: "4px 8px",
                    borderRadius: 6,
                    backgroundColor: token.colorSuccessBg,
                    color: token.colorSuccessText,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Approved {day.approvedCount}
                </div>
                <div
                  style={{
                    padding: "4px 8px",
                    borderRadius: 6,
                    backgroundColor: token.colorWarningBg,
                    color: token.colorWarningText,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Pending {day.pendingCount}
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: token.colorTextSecondary }}>
                  Top staff
                </div>
                <Text style={{ fontSize: 12 }}>
                  {day.topEmployees.slice(0, 3).join(", ")}
                  {day.topEmployees.length > 3 &&
                    ` +${day.topEmployees.length - 3} more`}
                </Text>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: "100%", padding: "16px 0" }}
    >
      {/* Controls */}
      <div>
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Location
              </Text>
              <Select
                style={{ width: "100%" }}
                value={selectedLocation}
                onChange={onLocationChange}
                options={[
                  { label: "All Locations", value: "all" },
                  ...availableLocations.map((loc) => ({
                    label: loc.name,
                    value: loc.key.toString(),
                  })),
                ]}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Week Navigation
              </Text>
              <Space style={{ width: "100%" }}>
                <Button
                  onClick={() => onWeekChange?.("prev")}
                  icon={<CalendarOutlined />}
                  size="small"
                >
                  Previous
                </Button>
                <Button onClick={() => onWeekChange?.("current")} size="small">
                  Current
                </Button>
                <Button
                  onClick={() => onWeekChange?.("next")}
                  icon={<CalendarOutlined />}
                  size="small"
                >
                  Next
                </Button>
              </Space>
            </Space>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  View
                </Text>
                <Segmented
                  block
                  value={viewMode}
                  onChange={(value) =>
                    setViewMode(value as "day" | "week-summary")
                  }
                  options={[
                    { label: "Day", value: "day" },
                    { label: "Summary", value: "week-summary" },
                  ]}
                />
              </div>
              {viewMode === "day" && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Day Selector
                  </Text>
                  <Segmented
                    value={selectedDay}
                    onChange={(value) => setSelectedDay(value as number)}
                    block
                    options={weekDates.map((date, index) => ({
                      label: (
                        <div style={{ padding: "1px 0", lineHeight: 1.1 }}>
                          <div style={{ fontSize: 11, fontWeight: 600 }}>
                            {date.format("ddd")}
                          </div>
                          <div style={{ fontSize: 12, opacity: 0.75 }}>
                            {date.format("M/D")}
                          </div>
                        </div>
                      ),
                      value: index,
                    }))}
                  />
                </div>
              )}
            </Space>
          </Col>
        </Row>
        <Row style={{ marginTop: 12 }}>
          <Col span={24} style={{ textAlign: "center" }}>
            <Text strong>Week of {weekStart.format("MMMM D, YYYY")}</Text>
          </Col>
        </Row>
      </div>

      {/* Color Legend Toggle */}
      <Card
        size="small"
        style={{
          marginBottom: 16,
          borderRadius: 8,
          backgroundColor: token.colorBgContainer,
          borderColor: token.colorBorder,
        }}
        bodyStyle={{ padding: "12px 16px" }}
      >
        <Space
          align="center"
          style={{ width: "100%", justifyContent: "space-between" }}
        >
          <Text strong style={{ fontSize: 14, color: token.colorText }}>
            Branch & Shift Type Colors
          </Text>
          <Space align="center">
            <Text style={{ fontSize: 13, color: token.colorTextSecondary }}>
              {showColorLegend ? "Hide" : "Show"} Legend
            </Text>
            <Switch
              checked={showColorLegend}
              onChange={setShowColorLegend}
              checkedChildren={<EyeOutlined />}
              unCheckedChildren={<EyeInvisibleOutlined />}
              size="small"
            />
          </Space>
        </Space>
      </Card>

      {/* Color Legend */}
      <LocationColorLegend visible={showColorLegend} locations={locations} />

      {/* Timeline content */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" tip="Loading schedule data..." />
        </div>
      ) : viewMode === "day" ? (
        renderDayView()
      ) : (
        renderManagerWeekView()
      )}
    </Space>
  );
};

export default ScheduleTimelineView;
